/**
 * Shared multi-select FP table used by Bulk Operations and Validate Sync Data.
 *
 * Pass the hook return values from `useFranchisePartnersFilters` and
 * `useBulkOperations` through to keep state ownership in the parent page.
 * `extraColumns` adds page-specific trailing columns (e.g. "Last validated"
 * for validate, "LS Data / Xero Data" for bulk operations).
 */
import React, { ReactNode } from 'react'
import {
  Box,
  Checkbox,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
} from '@mui/material'
import { FranchisePartner } from './hooks/useFranchisePartnersData'

export interface PartnerExtraColumn {
  key: string
  header: ReactNode
  align?: 'left' | 'right' | 'center'
  render: (fp: FranchisePartner) => ReactNode
}

interface PartnerSelectionTableProps {
  paginatedData: FranchisePartner[]
  filteredData: FranchisePartner[]
  page: number
  setPage: (page: number) => void
  rowsPerPage: number
  setRowsPerPage: (rowsPerPage: number) => void
  isSelected: (fpCode: string) => boolean
  isAllSelected: (data: FranchisePartner[]) => boolean
  isAllFilteredSelected: (data: FranchisePartner[]) => boolean
  isIndeterminate: (data: FranchisePartner[]) => boolean
  handleSelectFranchise: (fpCode: string) => void
  handleSelectAll: (data: FranchisePartner[]) => void
  handleSelectAllFiltered: (data: FranchisePartner[]) => void
  clearSelection: () => void
  extraColumns?: PartnerExtraColumn[]
  disabled?: boolean
  loading?: boolean
  rowsPerPageOptions?: number[]
}

const PartnerSelectionTable: React.FC<PartnerSelectionTableProps> = ({
  paginatedData,
  filteredData,
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  isSelected,
  isAllSelected,
  isAllFilteredSelected,
  isIndeterminate,
  handleSelectFranchise,
  handleSelectAll,
  handleSelectAllFiltered,
  clearSelection,
  extraColumns = [],
  disabled = false,
  loading = false,
  rowsPerPageOptions = [10, 25, 50, 100],
}) => {
  const baseColCount = 5 // checkbox + code + name + market + status
  const totalCols = baseColCount + extraColumns.length

  return (
    <TableContainer component={Paper} sx={{ mb: 2, opacity: disabled ? 0.6 : 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox
                indeterminate={isIndeterminate(paginatedData)}
                checked={isAllSelected(paginatedData)}
                onChange={() => handleSelectAll(paginatedData)}
                disabled={disabled}
              />
            </TableCell>
            <TableCell>FP Code</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Market</TableCell>
            <TableCell>Status</TableCell>
            {extraColumns.map((col) => (
              <TableCell key={col.key} align={col.align || 'left'}>{col.header}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        {isAllSelected(paginatedData) && filteredData.length > paginatedData.length && (
          <TableBody>
            <TableRow>
              <TableCell
                colSpan={totalCols}
                sx={{
                  textAlign: 'center',
                  backgroundColor: '#e3f2fd',
                  py: 1,
                  borderBottom: '1px solid #bbdefb',
                }}
              >
                {isAllFilteredSelected(filteredData) ? (
                  <Typography variant="body2">
                    All {filteredData.length} results selected.{' '}
                    <Typography
                      component="span"
                      variant="body2"
                      sx={{ color: '#1565c0', cursor: 'pointer', fontWeight: 500 }}
                      onClick={clearSelection}
                    >
                      Clear selection
                    </Typography>
                  </Typography>
                ) : (
                  <Typography variant="body2">
                    All {paginatedData.length} items on this page selected.{' '}
                    <Typography
                      component="span"
                      variant="body2"
                      sx={{ color: '#1565c0', cursor: 'pointer', fontWeight: 500 }}
                      onClick={() => handleSelectAllFiltered(filteredData)}
                    >
                      Select all {filteredData.length} results
                    </Typography>
                  </Typography>
                )}
              </TableCell>
            </TableRow>
          </TableBody>
        )}
        <TableBody>
          {paginatedData.map((fp) => (
            <TableRow
              key={fp.fp_code}
              hover
              onClick={() => !disabled && handleSelectFranchise(fp.fp_code)}
              sx={{ cursor: disabled ? 'default' : 'pointer' }}
            >
              <TableCell padding="checkbox">
                <Checkbox checked={isSelected(fp.fp_code)} disabled={disabled} />
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {fp.fp_code}
                </Typography>
              </TableCell>
              <TableCell>{fp.franchise_name}</TableCell>
              <TableCell>
                <Chip label={fp.country} size="small" variant="outlined" />
              </TableCell>
              <TableCell>
                <Chip
                  label={fp.fp_status}
                  size="small"
                  color={fp.fp_status === 'Active' ? 'success' : 'default'}
                />
              </TableCell>
              {extraColumns.map((col) => (
                <TableCell key={col.key} align={col.align || 'left'}>
                  {col.render(fp)}
                </TableCell>
              ))}
            </TableRow>
          ))}
          {paginatedData.length === 0 && (
            <TableRow>
              <TableCell colSpan={totalCols} align="center">
                <Typography variant="body2" color="textSecondary" sx={{ py: 2 }}>
                  {loading ? 'Loading...' : 'No franchise partners found'}
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        count={filteredData.length}
        page={page - 1}
        onPageChange={(_, newPage) => setPage(newPage + 1)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
        rowsPerPageOptions={rowsPerPageOptions}
      />
      <Box />
    </TableContainer>
  )
}

export default PartnerSelectionTable
